import React from 'react';
import PropTypes from 'prop-types';
import PrinterChip from './PrinterChip';
import style from './IdCardPrinterBoard.module.sass';

const formatDeliveredDate = (isoString) => {
  if (!isoString) return null;
  return new Date(isoString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const mostRecentDeliveredDate = (printers) => printers
  .map((printer) => printer.delivered_at)
  .filter(Boolean)
  .sort()
  .pop();

// This card is a plain (non-Droppable) Draggable -- react-beautiful-dnd can't
// reliably detect a printer dropped onto a Droppable nested inside a
// different-type Draggable, so a printer landing on this card is instead
// detected by cursor position in index.jsx (see hoveredSchoolId there) and
// highlighted here via isHoveredForPrinterDrop rather than rbd's own
// dropSnapshot. The `data-school-id` attribute is what that hit-test reads.
const SchoolCard = ({
  school, busyIds, moveLabel, onMove, onRemoveRoster, onUnpairPrinter, onRetirePrinter,
  isDragging, isHoveredForPrinterDrop, showDeliveredDate,
}) => {
  const isPending = busyIds.includes(`school-${school.school_id}`);
  const deliveredDate = showDeliveredDate ? formatDeliveredDate(mostRecentDeliveredDate(school.printers)) : null;
  const colorBarClass = showDeliveredDate ? style.cardBarGreen : style.cardBarYellow;

  return (
    <div
      data-school-id={school.school_id}
      className={`
        ${style.card} ${colorBarClass}
        ${isDragging ? style.cardDragging : ''}
        ${isHoveredForPrinterDrop ? style.dragOver : ''}
      `}
    >
      <div className={style.cardSchool}>{school.school_name}</div>
      {school.scode && <div className={style.cardScode}>{`SCODE ${school.scode}`}</div>}

      {school.printers.length === 0 && (
        <div className={style.emptyState}>No printer paired yet.</div>
      )}
      {school.printers.map((printer) => (
        <PrinterChip
          key={printer.id}
          printer={printer}
          disabled={isPending}
          onUnpair={onUnpairPrinter}
          onRetire={onRetirePrinter}
        />
      ))}

      {deliveredDate && <div className={style.cardDeliveredDate}>{`Delivered ${deliveredDate}`}</div>}

      <div className={style.cardActions}>
        {school.printers.length > 0 && (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={isPending}
            onClick={() => onMove(school)}
          >
            {moveLabel}
          </button>
        )}
        {onRemoveRoster && school.roster_id && school.printers.length === 0 && (
          <button
            type="button"
            className="btn btn-link btn-sm"
            disabled={isPending}
            onClick={() => onRemoveRoster(school)}
          >
            Remove
          </button>
        )}
      </div>
    </div>
  );
};

const printerShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  delivered: PropTypes.bool,
  delivered_at: PropTypes.string,
});

SchoolCard.propTypes = {
  school: PropTypes.shape({
    school_id: PropTypes.number.isRequired,
    school_name: PropTypes.string,
    scode: PropTypes.string,
    roster_id: PropTypes.number,
    printers: PropTypes.arrayOf(printerShape).isRequired,
  }).isRequired,
  busyIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  moveLabel: PropTypes.string.isRequired,
  onMove: PropTypes.func.isRequired,
  onRemoveRoster: PropTypes.func,
  onUnpairPrinter: PropTypes.func.isRequired,
  onRetirePrinter: PropTypes.func.isRequired,
  isDragging: PropTypes.bool,
  isHoveredForPrinterDrop: PropTypes.bool,
  showDeliveredDate: PropTypes.bool,
};

SchoolCard.defaultProps = {
  onRemoveRoster: null,
  isDragging: false,
  isHoveredForPrinterDrop: false,
  showDeliveredDate: false,
};

export default SchoolCard;
